import React from 'react';
import { Link, NavLink } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowRight,
  Briefcase,
  CalendarDays,
  Check,
  FileCheck2,
  FileSearch,
  FileText,
  FolderOpen,
  LockKeyhole,
  MessageSquare,
  Search,
  Scale,
  ShieldCheck,
  UsersRound,
} from 'lucide-react';
import logo from '../assets/logo.svg';
import './PublicLandingPage.css';

const featureSets = {
  overview: [
    {
      icon: MessageSquare,
      title: 'Legal AI assistant',
      text: 'Ask questions and understand Pakistani legal concepts in clear, practical language.',
    },
    {
      icon: FileSearch,
      title: 'Case summarization',
      text: 'Turn lengthy case documents into structured summaries with the key facts surfaced.',
    },
    {
      icon: FileCheck2,
      title: 'Contract compliance',
      text: 'Review contracts for important clauses, compliance concerns, and potential risks.',
    },
    {
      icon: Search,
      title: 'Advocate matching',
      text: 'Find advocates whose experience and practice areas fit the matter at hand.',
    },
  ],
  clients: [
    {
      icon: MessageSquare,
      title: 'Plain-language guidance',
      text: 'Start with your questions and understand legal concepts without unnecessary complexity.',
    },
    {
      icon: FileSearch,
      title: 'Document clarity',
      text: 'Surface the important facts and issues from lengthy case documents.',
    },
    {
      icon: Search,
      title: 'Relevant advocate matching',
      text: 'Connect with advocates whose experience aligns with your legal matter.',
    },
    {
      icon: UsersRound,
      title: 'Connected case journey',
      text: 'Keep advocate conversations, appointments, and case activity together.',
    },
  ],
  advocates: [
    {
      icon: FileSearch,
      title: 'Case intelligence',
      text: 'Summarize case documents and identify important facts before deeper review.',
    },
    {
      icon: FileCheck2,
      title: 'Compliance review',
      text: 'Inspect contracts for important provisions, obligations, and potential risks.',
    },
    {
      icon: UsersRound,
      title: 'Client coordination',
      text: 'Keep client conversations and matter details accessible from one workspace.',
    },
    {
      icon: Briefcase,
      title: 'Practice workspace',
      text: 'Coordinate active matters, schedules, documents, and daily legal work.',
    },
  ],
};

const reveal = {
  hidden: { opacity: 0, y: 38 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 1.25, ease: [0.16, 1, 0.3, 1] },
  },
};

const stagger = {
  hidden: {},
  visible: { transition: { delayChildren: 0.15, staggerChildren: 0.24 } },
};

const viewport = { once: true, amount: 0.2 };

const pageContent = {
  overview: {
    eyebrow: 'Legal clarity, powered by AI',
    title: 'Navigate legal matters with confidence.',
    description: "LawEase brings intelligent legal tools and trusted advocates into one secure platform, built for Pakistan's legal workflows.",
    primaryAction: 'Create your account',
    featureLabel: 'One connected platform',
    featureTitle: 'From uncertainty to a clear way forward.',
    featureText: 'Practical tools for understanding documents, finding support, and managing legal work.',
    trust: ['Secure workspace', 'For clients and advocates'],
  },
  clients: {
    eyebrow: 'A clearer legal journey',
    title: 'Understand your matter before taking the next step.',
    description: 'Ask questions, simplify legal documents, connect with the right advocate, and keep your matter organized in one place.',
    primaryAction: 'Start as a client',
    featureLabel: 'Built around your next step',
    featureTitle: 'Legal support that starts with understanding.',
    featureText: 'Turn confusion into practical actions with tools designed around the client journey.',
    trust: ['Plain-language guidance', 'Relevant advocate matching'],
  },
  advocates: {
    eyebrow: 'A focused legal workspace',
    title: 'Move legal work forward with greater focus.',
    description: 'Analyze documents, coordinate client matters, and manage daily legal work from one intelligent professional workspace.',
    primaryAction: 'Join as an advocate',
    featureLabel: 'Built for legal practice',
    featureTitle: 'Intelligent tools for demanding legal work.',
    featureText: 'Reduce document overhead and keep client service moving without fragmenting your workflow.',
    trust: ['Document intelligence', 'Connected client workspace'],
  },
};

function PublicLandingPage({ audience = 'overview' }) {
  const content = pageContent[audience];
  const visibleFeatures = featureSets[audience];

  return (
    <div className={`public-landing public-landing-${audience}`}>
      <motion.header
        className="public-nav"
        initial={{ opacity: 0, y: -16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 1.15, ease: [0.16, 1, 0.3, 1] }}
      >
        <Link className="public-brand" to="/" aria-label="LawEase home">
          <span className="public-brand-mark"><img src={logo} alt="" /></span>
          <span>LawEase</span>
        </Link>
        <nav className="public-nav-links" aria-label="Main navigation">
          <NavLink to="/" end className={({ isActive }) => isActive ? 'active' : ''}>Product</NavLink>
          <NavLink to="/clients" className={({ isActive }) => isActive ? 'active' : ''}>For clients</NavLink>
          <NavLink to="/advocates" className={({ isActive }) => isActive ? 'active' : ''}>For advocates</NavLink>
        </nav>
        <div className="public-nav-actions">
          <Link className="public-login-link" to="/login">Sign in</Link>
          <Link className="public-button public-button-small" to="/signup">
            Get started <ArrowRight size={16} />
          </Link>
        </div>
      </motion.header>

      <main id="top">
        <section className="public-hero">
          <motion.div className="public-hero-copy" initial="hidden" animate="visible" variants={stagger}>
            <motion.div className="public-eyebrow" variants={reveal}><Scale size={15} /> {content.eyebrow}</motion.div>
            <motion.h1 variants={reveal}>{content.title}</motion.h1>
            <motion.p variants={reveal}>{content.description}</motion.p>
            <motion.div className="public-hero-actions" variants={reveal}>
              <Link className="public-button" to="/signup">
                {content.primaryAction} <ArrowRight size={18} />
              </Link>
              <a className="public-button public-button-secondary" href="#features">Explore the platform</a>
            </motion.div>
            <motion.div className="public-trust-line" variants={reveal}>
              <span><ShieldCheck size={17} /> {content.trust[0]}</span>
              <span><UsersRound size={17} /> {content.trust[1]}</span>
            </motion.div>
          </motion.div>

          <motion.div
            className="public-product-preview"
            aria-label="LawEase product preview"
            initial={{ opacity: 0, x: 48 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 1.55, delay: 0.45, ease: [0.16, 1, 0.3, 1] }}
          >
            <div className="preview-window">
              <div className="preview-topbar">
                <div className="preview-brand"><Scale size={18} /> LawEase workspace</div>
                <div className="preview-avatar">AK</div>
              </div>
              <div className="preview-body">
                <aside className="preview-sidebar">
                  <span className="preview-side-active"><Briefcase size={16} /> Overview</span>
                  <span><MessageSquare size={16} /> Legal assistant</span>
                  <span><FileSearch size={16} /> Case AI</span>
                  <span><FileCheck2 size={16} /> Compliance</span>
                </aside>
                <div className="preview-content">
                  <div className="preview-heading">
                    <div>
                      <small>CASE INTELLIGENCE</small>
                      <strong>Document analysis</strong>
                    </div>
                    <span className="preview-status"><Check size={13} /> Complete</span>
                  </div>
                  <div className="preview-summary-card">
                    <div className="preview-summary-icon"><FileText size={20} /></div>
                    <div>
                      <small>AI SUMMARY</small>
                      <p>The petition challenges an administrative order and identifies three primary legal issues.</p>
                    </div>
                  </div>
                  <div className="preview-grid">
                    <div className="preview-metric">
                      <span>Key issues</span>
                      <strong>03</strong>
                      <div className="preview-bar"><i /></div>
                    </div>
                    <div className="preview-metric">
                      <span>Relevant provisions</span>
                      <strong>07</strong>
                      <div className="preview-bar preview-bar-teal"><i /></div>
                    </div>
                  </div>
                  <div className="preview-insight">
                    <ShieldCheck size={18} />
                    <span><strong>Clear next steps</strong> Review the identified provisions and connect with a relevant advocate.</span>
                  </div>
                </div>
              </div>
            </div>
            <motion.div className="public-floating-card public-floating-card-one" animate={{ y: [0, -7, 0] }} transition={{ duration: 7, repeat: Infinity, ease: 'easeInOut' }}>
              <MessageSquare size={18} />
              <span><strong>Legal assistant</strong> Ready to help</span>
            </motion.div>
            <motion.div className="public-floating-card public-floating-card-two" animate={{ y: [0, 7, 0] }} transition={{ duration: 8, repeat: Infinity, ease: 'easeInOut' }}>
              <LockKeyhole size={18} />
              <span><strong>Private</strong> Secure by design</span>
            </motion.div>
          </motion.div>
        </section>

        <section className="public-section public-feature-section" id="features">
          <motion.div className="public-section-heading" variants={reveal} initial="hidden" whileInView="visible" viewport={viewport}>
            <span>{content.featureLabel}</span>
            <h2>{content.featureTitle}</h2>
            <p>{content.featureText}</p>
          </motion.div>
          <motion.div key={audience} className="public-feature-grid" variants={stagger} initial="hidden" whileInView="visible" viewport={viewport}>
            {visibleFeatures.map(({ icon: Icon, title, text }) => (
              <motion.article className="public-feature-card" key={title} variants={reveal} whileHover={{ y: -8 }}>
                <div className="public-feature-icon"><Icon size={22} /></div>
                <h3>{title}</h3>
                <p>{text}</p>
              </motion.article>
            ))}
          </motion.div>
        </section>

        <motion.section key={audience} className={`public-section public-audience-section ${audience !== 'overview' ? 'public-audience-section-single' : ''}`} variants={stagger} initial="hidden" whileInView="visible" viewport={viewport}>
          {audience !== 'advocates' && (
          <motion.article
            className="public-audience-card public-client-card"
            id="clients"
            variants={reveal}
            whileHover={{ y: -8 }}
            transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
          >
            <div className="public-audience-header">
              <div className="public-audience-icon"><UsersRound size={24} /></div>
              <span>For clients</span>
            </div>
            <h2>A clear path through your legal matter.</h2>
            <p>Move from your first question to professional support without losing track of the details.</p>
            <ol className="public-client-steps">
              <li><strong>01</strong><span><b>Understand</b> Ask questions and simplify documents.</span></li>
              <li><strong>02</strong><span><b>Connect</b> Find an advocate suited to your matter.</span></li>
              <li><strong>03</strong><span><b>Move forward</b> Keep cases and conversations organized.</span></li>
            </ol>
            <Link to="/signup">Create a client account <ArrowRight size={17} /></Link>
          </motion.article>
          )}
          {audience !== 'clients' && (
          <motion.article
            className="public-audience-card public-advocate-card"
            id="advocates"
            variants={reveal}
            whileHover={{ y: -8 }}
            transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
          >
            <div className="public-advocate-card-copy">
              <div className="public-audience-header">
                <div className="public-audience-icon"><Briefcase size={24} /></div>
                <span>For advocates</span>
                <small className="public-advocate-status"><span /> Practice ready</small>
              </div>
              <h2>Run legal work from one focused workspace.</h2>
              <p>Use intelligent document tools and a focused workspace to serve clients efficiently.</p>
              <Link to="/signup">Join as an advocate <ArrowRight size={17} /></Link>
            </div>
            <div className="public-practice-desk">
              <div className="public-practice-desk-header">
                <div>
                  <small>MONDAY, 16 JUNE</small>
                  <strong>Today at the practice</strong>
                </div>
                <span><Briefcase size={15} /> 4 active matters</span>
              </div>
              <div className="public-practice-docket">
                <div className="public-docket-item public-docket-item-primary">
                  <span className="public-docket-icon"><FolderOpen size={18} /></span>
                  <div><small>MATTER IN PROGRESS</small><strong>Constitutional petition</strong><p>Review supporting documents</p></div>
                  <em>Open matter</em>
                </div>
                <div className="public-docket-item">
                  <span className="public-docket-icon"><CalendarDays size={18} /></span>
                  <div><small>NEXT HEARING</small><strong>District Court</strong><p>Wednesday, 10:30 AM</p></div>
                </div>
                <div className="public-docket-item">
                  <span className="public-docket-icon"><FileText size={18} /></span>
                  <div><small>DOCUMENT REVIEW</small><strong>Service agreement</strong><p>Compliance review pending</p></div>
                </div>
                <div className="public-docket-item">
                  <span className="public-docket-icon"><MessageSquare size={18} /></span>
                  <div><small>CLIENT UPDATE</small><strong>New message received</strong><p>Reply when ready</p></div>
                </div>
              </div>
            </div>
          </motion.article>
          )}
        </motion.section>

        <motion.section className="public-section public-final-cta" variants={reveal} initial="hidden" whileInView="visible" viewport={viewport}>
          <div>
            <span>{audience === 'advocates' ? 'Build your practice' : 'Start with clarity'}</span>
            <h2>{audience === 'clients' ? 'Your next step can be clearer.' : audience === 'advocates' ? 'Your focused legal workspace is ready.' : 'Your legal workspace is ready.'}</h2>
            <p>{audience === 'advocates' ? 'Create an advocate account and bring your legal work into focus.' : 'Create an account and bring the next step into focus.'}</p>
          </div>
          <Link className="public-button public-button-light" to="/signup">
            {content.primaryAction} <ArrowRight size={18} />
          </Link>
        </motion.section>
      </main>

      <footer className="public-footer">
        <div className="public-footer-main">
          <a className="public-brand" href="#top">
            <span className="public-brand-mark"><img src={logo} alt="" /></span>
            <span>LawEase</span>
          </a>
          <p>AI-powered legal tools for clearer decisions.</p>
          <span>LawEase does not replace professional legal advice.</span>
        </div>
        <div className="public-team-credit">
          <span>Designed and developed by Team LawEase</span>
          <div className="public-team-members">
            <a href="https://alicancode.com" target="_blank" rel="noreferrer">Ali Abdullah</a>
            <a href="https://www.linkedin.com/in/abdul-rehman-608449365/" target="_blank" rel="noreferrer">Abdul Rehman</a>
            <a href="https://www.linkedin.com/in/mustafaawann/" target="_blank" rel="noreferrer">Mustafa Awan</a>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default PublicLandingPage;
